import { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../lib/firebase";
import toast from "react-hot-toast";

export const MemoryForm = () => {
  const [from, setFrom] = useState("");
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [privateOnly, setPrivateOnly] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ❗ Validation: prevent empty upload
    if (!file && text.trim() === "") {
      toast.error("Voeg een herinnering toe: tekst of foto/video is vereist.");
      return;
    }

    setLoading(true);

    try {
      let mediaUrl = null;
      let type: "image" | "video" | "text" = "text";

      if (file) {
        const fileRef = ref(storage, `memories/${Date.now()}-${file.name}`);
        await uploadBytes(fileRef, file);
        mediaUrl = await getDownloadURL(fileRef);
        type = file.type.startsWith("video") ? "video" : "image";
      }

      await addDoc(collection(db, "memories"), {
        from: from || null,
        type,
        text,
        media: type !== "text" ? mediaUrl : null,
        private: privateOnly,
        date: serverTimestamp(),
      });

      // Clear form
      setFrom("");
      setText("");
      setFile(null);
      setPrivateOnly(false);

      toast.success("Herinnering succesvol gedeeld ❤️");
    } catch (err) {
      console.error(err);
      const msg =
        err instanceof Error ? err.message : "Failed to upload memory";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ maxWidth: 600, margin: "auto", mt: 4, boxShadow: 3 }}>
      <CardContent>
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label="Naam (optioneel)"
            fullWidth
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Herinnering / tekst"
            fullWidth
            multiline
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Button variant="outlined" component="label" fullWidth sx={{ mb: 2 }}>
            Voeg foto of video toe
            <input
              type="file"
              hidden
              accept="image/*,video/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </Button>

          {file && <Typography variant="body2">📎 {file.name}</Typography>}

          <FormControlLabel
            control={
              <Checkbox
                checked={privateOnly}
                onChange={(e) => setPrivateOnly(e.target.checked)}
              />
            }
            label="Alleen zichtbaar voor familie"
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              backgroundColor: "#3c5875",
              color: "white",
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              py: 1.2,
              borderRadius: "30px",
              mb: 2,
              mt: 2,
              "&:hover": { backgroundColor: "#2c455a" },
            }}
          >
            {loading ? "Aan het verzenden..." : "Herinnering delen"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
